import { IconNames } from '@blueprintjs/icons';
import { skipToken } from '@reduxjs/toolkit/query';
import { ActionButtonText } from '@strata/react-buttons';
import { InlineMessage } from '@strata/react-inline-message';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router';

import { getTransitionMessage, TransitionViewContext } from 'services/treatmentTransitionHelpers';
import { useGetAllProductServicesQuery } from 'store/api/referenceApi';
import { useGetReferralTransitionInfoQuery } from 'store/api/treatmentApi';

const TherapyTransitionInlineMessage = ({
    message,
    linkTarget = null,
    backToDestination = undefined,
}) => {
    const navigate = useNavigate();

    const handleNavigate = () => {
        if (!linkTarget) {
            return;
        }

        navigate(linkTarget.url, {
            state: { backToDestination },
        });
    };

    return (
        <InlineMessage
            intent={InlineMessage.Intent.WARNING}
            wrapText
            label={
                <>
                    {message}
                    {linkTarget && (
                        <>
                            {' '}
                            <ActionButtonText
                                label={linkTarget.label}
                                rightIcon={IconNames.DOUBLE_CHEVRON_RIGHT}
                                onClick={handleNavigate}
                                style={{ display: 'inline-flex', verticalAlign: 'baseline' }}
                            />
                        </>
                    )}
                </>
            }
        />
    );
};

TherapyTransitionInlineMessage.propTypes = {
    message: PropTypes.string.isRequired,
    linkTarget: PropTypes.shape({
        url: PropTypes.string.isRequired,
        label: PropTypes.string.isRequired,
    }),
    backToDestination: PropTypes.shape({
        text: PropTypes.string,
    }),
};

export const TherapyTransitionInlineMessages = ({
    payerId = undefined,
    referralId = undefined,
    backToDestination = undefined,
    viewContext,
}) => {
    const { data: transitions } = useGetReferralTransitionInfoQuery(
        payerId && referralId ? { payerId, referralId } : skipToken,
    );
    const { data: allProductServicesData } = useGetAllProductServicesQuery();
    const { productServiceMap } = allProductServicesData ?? {};

    const transitionContent = getTransitionMessage({
        transitions,
        productServiceMap,
        payerId,
        viewContext,
    });

    if (!transitionContent) {
        return null;
    }

    return (
        <TherapyTransitionInlineMessage
            message={transitionContent.message}
            linkTarget={transitionContent.linkTarget}
            backToDestination={backToDestination}
        />
    );
};

TherapyTransitionInlineMessages.propTypes = {
    payerId: PropTypes.string,
    referralId: PropTypes.string,
    backToDestination: PropTypes.shape({
        text: PropTypes.string,
    }),
    viewContext: PropTypes.oneOf(Object.values(TransitionViewContext)).isRequired,
};
